import {
  SMB_BUSINESS_ACCOUNT_HK_ROLES,
  SMB_BUSINESS_ACCOUNT_UK_ROLES,
} from '@rapyd/cp-core/dist/utils/consts/BusinessAccount';
import { getOrganizationDetails, getOrganizationRole } from 'src/utils/organizationDetails.utils';
import UserEnvironments from '@rapyd/cp-core/dist/utils/consts/UserEnvironments';
import {
  KORTA_RAPYD_ROLE,
  NOT_RAPYD_ROLES,
  VALITOR_ROLE,
} from 'src/consts/organizationRoles.consts';
import { PermissionsProps } from '@rapyd/cp-core';
import isPermittedRole from '@rapyd/cp-core/dist/utils/permissions.utils';
import { RapydResponse } from 'src/types/api.types';
import { filter, includes, map, some } from 'lodash-es';
import { currencyCodeToCountryCode } from '@rapyd/cp-core/dist/utils';
import { UserInfoResponse } from 'src/types/userData.types';
import { SMB_BUSINESS_ACCOUNT_ROLES } from '@rapyd/cp-core/dist/utils/consts/BusinessAccount';

export const isSMBBusinessAccountUserSelector = (userInfo: UserInfoResponse): boolean =>
  includes(SMB_BUSINESS_ACCOUNT_ROLES, getOrganizationRole(userInfo));

export const isSMBBusinessAccountHkUserSelector = (userInfo: UserInfoResponse): boolean =>
  isSMBBusinessAccountUserSelector(userInfo) &&
  includes(SMB_BUSINESS_ACCOUNT_HK_ROLES, getOrganizationRole(userInfo));

export const isSMBBusinessAccountUKSelector = (userInfo: UserInfoResponse): boolean =>
  isSMBBusinessAccountUserSelector(userInfo) &&
  includes(SMB_BUSINESS_ACCOUNT_UK_ROLES, getOrganizationRole(userInfo));

export const getUserEnvSelector = (userInfo: UserInfoResponse) =>
  userInfo?.user_data?.mpu_environment || UserEnvironments.PRODUCTION;

export const isOrganizationRoleKortaRapyd = (userInfo: UserInfoResponse): boolean =>
  getOrganizationDetails(userInfo).role === KORTA_RAPYD_ROLE;
export const getUserRolePermissions = (userInfo: UserInfoResponse) => {
  const { user_data: userData } = userInfo;
  return {
    userRolePermissions: userData.role_permissions,
    userRoleProdPermissions: userData.prod_role_permissions,
  };
};

export const isPermittedUserRole = (
  userInfo: UserInfoResponse,
  { permissions, permissionsLogicalOperator, checkProductionPermissions }: PermissionsProps,
) => {
  const { userRolePermissions, userRoleProdPermissions } = getUserRolePermissions(userInfo);
  const userPermissions = checkProductionPermissions
    ? userRoleProdPermissions
    : userRolePermissions;
  return isPermittedRole(userPermissions, permissions, permissionsLogicalOperator);
};

export const getClientCurrencies = (userInfoResponse: RapydResponse<UserInfoResponse>) =>
  userInfoResponse?.data?.client_currencies;

export const getUserData = (userInfoResponse: RapydResponse<UserInfoResponse>) =>
  userInfoResponse?.data?.user_data;

export const getUserSettings = (userInfoResponse: RapydResponse<UserInfoResponse>) =>
  getUserData(userInfoResponse)?.settings || {};

export const getUserHomeSettings = (userInfoResponse: RapydResponse<UserInfoResponse>) =>
  getUserSettings(userInfoResponse)?.home_settings;

export const getUserHomeOverviewSettings = (userInfoResponse: RapydResponse<UserInfoResponse>) =>
  getUserHomeSettings(userInfoResponse)?.overview;

export const getClientCurrencyOptionsAsCodes = (
  userInfoResponse: RapydResponse<UserInfoResponse>,
) => {
  const clientCurrencies = getClientCurrencies(userInfoResponse);

  return map(clientCurrencies, currencyCode => ({
    value: currencyCode,
    label: currencyCode,
    country: currencyCodeToCountryCode(currencyCode),
  }));
};

export const getHamburgerMenuUserData = (userInfoResponse: RapydResponse<UserInfoResponse>) => {
  const { version, user_data: userData, merchant_data: merchantData } = userInfoResponse.data;

  return {
    fullName: `${userData.first_name} ${userData.last_name}`,
    email: userData.email,
    roleAlias: userData.role_alias,
    merchantAlias: merchantData.merchant_alias,
    version,
  };
};

export const getIsSandboxOrOffline = (userInfoResponse: RapydResponse<UserInfoResponse>): boolean =>
  getUserEnvSelector(userInfoResponse.data) !== UserEnvironments.PRODUCTION;

export const getConnectedOrganizations = (userInfoResponse: RapydResponse<UserInfoResponse>) =>
  userInfoResponse?.data?.user_data?.connected_organizations;

export const getRapydConnectedOrganizations = (
  userInfoResponse: RapydResponse<UserInfoResponse>,
) => {
  const connectedOrganizations = getConnectedOrganizations(userInfoResponse);

  return filter(connectedOrganizations, organization => {
    const { role_name: roleName } = organization;

    return !NOT_RAPYD_ROLES.includes(roleName);
  });
};

export const getIsHaveValitorMerchant = (userInfoResponse: RapydResponse<UserInfoResponse>) => {
  const connectedOrganizations = getConnectedOrganizations(userInfoResponse);

  return some(connectedOrganizations, organization => {
    const { role_name: roleName } = organization;

    return roleName === VALITOR_ROLE;
  });
};

export const getProductionOrganizationId = (userInfoResponse: RapydResponse<UserInfoResponse>) => {
  const { organization_details: orgDetails } = userInfoResponse.data;

  return orgDetails.prod_id;
};
